import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { c } from 'ttag';

import {
    AuthenticationStore,
    Button,
    LegalDocument,
    LoaderPage,
    getLegalDocument,
    useAuthentication,
    useI18n,
    useInsideApi,
    useLocalState,
} from '@freelancelabs/components';
import { LogoFreelance } from '@freelancelabs/icons';
import { AppName, acceptCgu, getUser, redirectToApp, removeItem } from '@freelancelabs/shared';

import { fetchMfaRequirement } from '../../api/mfaRequirement';
import { AlertType, PublicRoutes, redirectAfterLoginKey } from '../../constants';
import MfaSetupPage from './MfaSetup';

interface AppScope {
    appName: AppName | string;
    isConfirmed: boolean;
}

const ALLOWED_STATES = ['FORCE_CHANGE_PASSWORD', 'CONFIRMED', 'NEW_PASSWORD_REQUIRED'];

/**
 * Scope names that open Client Hub. These come from inside-api's app registry, not from
 * AppName; the two vocabularies look alike but differ. CLIENT_HUB_API and INSIDE_CLIENT_API
 * are the same scope under its new and old names, both kept until no account carries the old
 * one. This guard only says whether the person may log in, not which app.
 */
const PORTAL_SCOPE_NAMES: string[] = ['ACCOUNT', 'INSIDE_CLIENT_API', 'CLIENT_HUB_API', AppName.PORTAIL_CLIENT];

const RedirectMiddlewarePage = () => {
    const navigate = useNavigate();
    const { logout, getAuthorizationToken, fetchMfaSettings, isFederatedSession } =
        useAuthentication() as AuthenticationStore;
    const api = useInsideApi();
    const { localeCode } = useI18n();
    const [lastCgu, setLastCgu] = useState<LegalDocument>();
    const [isLoading, setIsLoading] = useState(false);
    const [isInitialized, setIsInitialized] = useState(false);
    const [redirectAfterLogin] = useLocalState('', redirectAfterLoginKey);
    /** MFA barrier tripped: the setup screen replaces this page. */
    const [mfaRequired, setMfaRequired] = useState(false);
    /** The person's address, used to name the entry in their authenticator app. */
    const [email, setEmail] = useState('');

    const reject = async () => {
        // Clear the stored destination on refusal too, or the next successful login
        // (possibly another person on a shared machine) inherits it.
        removeItem(redirectAfterLoginKey);
        await logout();
        navigate(PublicRoutes.LOGIN, {
            replace: true,
            state: {
                withAlert: AlertType.APP_UNAUTHORIZED,
            },
        });
    };

    /**
     * The MFA barrier, posed here only, on both paths (effect + accept()), after the CGU,
     * and failing open on a read failure.
     */
    const mfaBlocks = async (federated: boolean) => {
        // No MFA for SSO sessions: the IdP already asked for a second factor. This is a fact
        // of the session, not the person; passed in as an argument so this guard cannot become
        // unreachable behind an identical test placed above it.
        if (federated) {
            return false;
        }

        const token = await getAuthorizationToken();
        if (!token) {
            return false;
        }
        const { required } = await fetchMfaRequirement(token);
        if (!required) {
            return false;
        }
        try {
            const { totp } = await fetchMfaSettings();
            return !totp;
        } catch {
            // Pool read failed: do not block on it.
            return false;
        }
    };

    // Real departure, no re-check: re-entering would ask the pool to confirm an enrolment
    // from a second ago, and a lagging read would bounce the person back to the setup screen.
    const goToApp = (federated = false) => {
        if (redirectAfterLogin) {
            // Consume the destination; removeItem, not the hook setter which writes in an
            // effect that would not run before navigation.
            removeItem(redirectAfterLoginKey);
            window.location.replace(redirectAfterLogin);
            return;
        }
        // Internal users go to Hub Admin; destination inferred from the federated session
        // (SSO=internal), which breaks once a client brings its own IdP. redirectAfterLogin
        // above takes precedence.
        redirectToApp(federated ? AppName.CLIENT_BO : AppName.PORTAIL_CLIENT);
    };

    const redirect = async () => {
        // Read once and passed to both: same fact, and two reads could disagree on one navigation.
        const federated = await isFederatedSession();
        if (await mfaBlocks(federated)) {
            setMfaRequired(true);
            return;
        }
        goToApp(federated);
    };

    const accept = async () => {
        if (lastCgu === undefined) {
            return;
        }
        setIsLoading(true);
        await api(acceptCgu({ versionName: lastCgu.version }));
        // Awaited: redirect now queries two services before leaving; releasing the button
        // early would leave the page clickable while the barrier is being decided.
        await redirect();
        setIsLoading(false);
    };

    useEffect(() => {
        const run = async () => {
            const locale = localeCode.slice(0, 2).toLowerCase() as 'en' | 'fr'; // @later mv to shared i18n
            const cgu = getLegalDocument({ type: 'cgu', locale });
            const user = await api(getUser());
            setEmail(user.email ?? '');
            if (!ALLOWED_STATES.includes(user.state)) {
                await reject();
                return;
            }
            // All opening scopes, not the first encountered: a person carries several and
            // only some are confirmed, so the response order must not decide admission.
            const portalScopes = user.appScopes.filter((appScope: AppScope) =>
                PORTAL_SCOPE_NAMES.includes(appScope.appName)
            );
            // Two distinct refusals: no scope (stranger to Client Hub) vs an unconfirmed
            // scope (activation never finished).
            if (portalScopes.length === 0) {
                await reject();
                return;
            }
            if (!portalScopes.some((appScope: AppScope) => appScope.isConfirmed)) {
                await reject();
                return;
            }
            setLastCgu(cgu);
            if (user.termsAcceptance === cgu.version) {
                await redirect();
                return;
            }
            setIsInitialized(true);
        };
        void run();
    }, []);

    // Barrier renders before loading/CGU: we only reach here via redirect, so CGU are
    // settled; rendering rather than navigating avoids a back-button skipping it.
    if (mfaRequired) {
        return <MfaSetupPage accountName={email} onDone={goToApp} />;
    }

    return (
        <>
            {!isInitialized ? (
                <LoaderPage />
            ) : (
                <main>
                    <div className="flex justify-center p-6">
                        <LogoFreelance size={96} />
                    </div>
                    <div className="pb-12 pt-6">
                        <div className="max-w-3xl w-full mx-auto pb-6 px-6">
                            <h1 className="text-base text-text-800">
                                {c('CGU')
                                    .t`To take advantage of our online services, you must first accept their general conditions of use.`}
                            </h1>
                        </div>
                        <div className="max-w-3xl w-full mx-auto px-6 pb-6">
                            <hr />
                            <article className="flex flex-col items-center">
                                {lastCgu?.document && (
                                    <div
                                        className="prose prose-sm md:prose-base max-w-full break-words"
                                        dangerouslySetInnerHTML={{ __html: lastCgu.document }}
                                    />
                                )}
                            </article>
                        </div>
                    </div>
                    <div className="sticky bottom-0 border-t bg-background-norm">
                        <div className="max-w-3xl w-full mx-auto px-6 py-4 flex items-center gap-6 justify-between">
                            <Button onClick={logout} category="weak">
                                {c('CGU').t`Decline and exit`}
                            </Button>
                            <Button onClick={accept} loading={isLoading}>
                                {c('CGU').t`I agree`}
                            </Button>
                        </div>
                    </div>
                </main>
            )}
        </>
    );
};

export default RedirectMiddlewarePage;
